import React, { RefAttributes, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import Select, { createFilter, ActionMeta } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Box, Tooltip } from '@mui/material';
import { FormComponentValue, prepareFormComponentValues } from 'components/common/form/layout/control';
import { ReactSelectProps, useReactSelectStyles } from './index';
import ReactSelectVirtualMenuList from 'components/common/react-select/parts/ReactSelectVirtualMenuList';
import { ReactSelectOption } from './parts/ReactSelectOption';
import { ReactSelectMultiValue } from './parts/ReactSelectMultiValue';
import IconHandler from 'components/common/icon/IconHandler';
import { ReactSelectDropdownIndicator } from './parts/ReactSelectDropdownIndicator';
import { ReactSelectMultiValueRemove } from 'components/common/react-select/parts/ReactSelectMultiValueRemove';
import { ReactSelectSingleValue } from './parts/ReactSelectSingleValue';
import { ReactSelectSelectContainer } from 'components/common/react-select/parts/ReactSelectSelectContainer';
import { ReactSelectClearIndicator } from 'components/common/react-select/parts/ReactSelectClearIndicator';
import AsyncSelect from 'react-select/async';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { instance } from 'api/api';
import useBundleTranslation from 'i18n';

function useTimeOut(delay: number, callback: Function): Function {
    const [timer, setTimer] = useState<number>(0);
    return function () {
        if (timer) {
            window.clearTimeout(timer);
        }
        const args = arguments;
        setTimer(
            window.setTimeout(() => {
                callback(...args);
            }, delay),
        );
    };
}

function ReactSelect(props: ReactSelectProps) {
    const { t } = useBundleTranslation('main');
    const viewSettings = useReactSelectStyles();

    const getComponent = (props: ReactSelectProps) => {
        if (props.async) {
            return props.creatable ? AsyncCreatableSelect : AsyncSelect;
        }
        return props.creatable ? CreatableSelect : Select;
    };

    const SelectComponent = getComponent(props);
    const OptionComponent = typeof props.optionComponent != 'undefined' ? props.optionComponent : ReactSelectOption;

    const [optionsList, setOptionsList] = useState<FormComponentValue[]>([]);
    const [currentSelectValue, setCurrentSelectValue] = useState<any>();
    const [inputValue, setInputValue] = useState<string>('');

    useLayoutEffect(() => {
        const wrappedValue = Array.isArray(props.value) ? props.value : [props.value];
        const defaultData = props.creatable || props.async ? optionsList : props.data;
        let defaultValue: any = [];

        const addSelectedValue = (element: any) => {
            if (wrappedValue.includes(Number(element.value)) || wrappedValue.includes(String(element.value))) {
                defaultValue.push(element);
            }
        };

        //process also grouped  format data
        defaultData.forEach((el) => {
            if (Array.isArray(el.options) && el.options.length) {
                el.options.forEach((groupEl) => {
                    addSelectedValue(groupEl);
                });
            } else {
                addSelectedValue(el);
            }
        });

        setCurrentSelectValue(defaultValue);
    }, [props.value, props.data, props.creatable, optionsList]);

    useLayoutEffect(() => {
        const visibleOptions = props.data.filter((el) => !el.hidden);

        const options = visibleOptions.map((item) => {
            const customItem = { ...item };

            if (item.props?.icon) {
                customItem.icon = <IconHandler icon={item.props?.icon} />;
            }

            if (item.props?.tooltip) {
                customItem.tooltip = item.props?.tooltip;
            }

            return { ...customItem };
        });
        setOptionsList(options);
    }, [props.data]);

    const handleInputChange = function (newValue: string) {
        setInputValue(newValue);
        if (props.onInputChange) props.onInputChange(newValue);
    };

    const updateOptionsList = useCallback(
        (nextOptions: FormComponentValue[] = []) => {
            const newOptions: FormComponentValue[] = nextOptions
                .filter((item: FormComponentValue) => item.__isNew__)
                .map((item: FormComponentValue) => ({
                    label: item.label,
                    value: item.value,
                }));

            if (newOptions.length) {
                setOptionsList([...optionsList, ...newOptions]);
            }
        },
        [setOptionsList, optionsList],
    );

    const handleChange = function (newValue: any, actionMeta: ActionMeta<FormComponentValue>) {
        if (props.creatable) {
            const optionsToUpdate = Array.isArray(newValue) ? newValue : newValue ? [newValue] : [];
            updateOptionsList(optionsToUpdate);
        }

        const value = props.isMulti
            ? newValue.map((el: FormComponentValue) => el.value)
            : newValue == null
              ? ''
              : newValue.value;

        setInputValue('');
        props.update(value, newValue, actionMeta);
    };

    // @ts-ignore
    const selectRef = useRef<RefAttributes>(null);
    // Set direction of dropdown
    let menuPlacement = 'bottom';
    if (selectRef.current) {
        const offsetTop = selectRef.current.controlRef.getBoundingClientRect().top;
        const totalHeight = document.body.scrollHeight;
        if (totalHeight - offsetTop < 200) {
            menuPlacement = 'top';
        }
    }

    useEffect(() => {
        if (selectRef.current && props.dataTest) {
            selectRef.current.inputRef.setAttribute('data-test', `${props?.dataTest}_select`);
        }
    }, [props.dataTest]);

    let components: any = {
        SelectContainer: ReactSelectSelectContainer,
        Option: OptionComponent,
        SingleValue: ReactSelectSingleValue,
        MultiValue: ReactSelectMultiValue,
        DropdownIndicator: ReactSelectDropdownIndicator,
        MultiValueRemove: ReactSelectMultiValueRemove,
        ClearIndicator: ReactSelectClearIndicator,
    };

    let groupedOptionsList: Array<any> = [];
    let valueGroups: Array<string> = [];
    optionsList.forEach((option) => {
        if (option?.valueGroup) {
            const groupName = String(option.valueGroup);
            if (!valueGroups.includes(groupName)) {
                valueGroups.push(groupName);
            }
        }
    });
    if (valueGroups.length) {
        groupedOptionsList = valueGroups.map((group) => ({
            label: group,
            options: optionsList.filter((opt) => opt.valueGroup == group),
        }));
        groupedOptionsList = optionsList.filter((option) => !option?.valueGroup).concat(groupedOptionsList);
    } else {
        if (props.applyVirtualList)
            components.MenuList = (props: any) => <ReactSelectVirtualMenuList {...props} viewSettings={viewSettings} />;
    }

    const [isOpenWatcher, setIsOpenWatcher] = useState(0);
    const [showTooltip, setShowTooltip] = useState(false);
    useEffect(() => {
        if (!props.tooltip) {
            return;
        }

        setIsOpenWatcher(
            window.setInterval(() => {
                setShowTooltip(!selectRef.current?.props?.menuIsOpen);
            }, 100),
        );
        return () => window.clearInterval(isOpenWatcher);
    }, []);

    components = { ...components, ...(props.overrideComponents ?? {}) };
    const containerRef = useRef();
    let selectParams: any = {
        ref: selectRef,
        isDisabled: props.disabled,
        value: currentSelectValue,
        inputValue: inputValue,
        filterOption: createFilter({ ignoreAccents: false }),
        options: valueGroups.length ? groupedOptionsList : optionsList,
        isLoading: props.isLoading,
        onMenuScrollToBottom: () => {},
        onInputChange: handleInputChange,
        captureMenuScroll: false,
        classNamePrefix: 'custom-select',
        onChange: handleChange,
        components: components,
        isMulti: props.isMulti,
        isOptionDisabled: (option: FormComponentValue) => Boolean(option.disabled),
        menuPortalTarget: props.customPortalTarget ?? (props.customPortalTarget === null ? undefined : document.body),
        // menuPortalTarget:{containerRef.current}
        theme: viewSettings.theme,
        styles: viewSettings.styles,
        instanceId: props.name,
        placeholder: props.placeholder ?? t('select___'),
        onMenuOpen: props.onMenuOpen,
        onMenuClose: props.onMenuClose,
        'data-test': props.dataTest ?? props.id ?? props.name,
        className: props.className,
        isClearable: props.isClearable,
        name: props.name,
        // @ts-ignore
        isMultiOneRowMode: props.isMultiOneRowMode,
        getOptionLabel: props.getOptionLabel,
        closeMenuOnSelect: props.closeMenuOnSelect !== false,
        noOptionsMessage: () => t('select_not_options'),
        classNames: props.classNames,
    };

    const asyncCall = (callback: Function, inputValue: string) => {
        instance
            //@ts-ignore
            .get(props.async, { params: { ...props.urlParams, pattern: inputValue } })
            .then((response) => {
                if (response.data.data) {
                    const values = prepareFormComponentValues(response.data.data);
                    callback(values);

                    const valuesToAdd: Array<FormComponentValue> = [];
                    values.forEach((v) => {
                        if (optionsList.findIndex((o) => o.value == v.value) == -1) {
                            valuesToAdd.push(v);
                        }
                    });
                    // Add new values to existing list
                    if (valuesToAdd.length) {
                        setOptionsList(optionsList.concat(valuesToAdd));
                    }
                }
            });
    };
    const asyncInputChange = useTimeOut(500, asyncCall);
    if (props.async) {
        selectParams = {
            ...selectParams,
            // cacheOptions: true,
            loadOptions: (inputValue: string, callback: (options: Array<FormComponentValue>) => void) => {
                asyncInputChange(callback, inputValue);
            },
            defaultOptions: optionsList,
        };
    }

    return (
        <Box
            ref={containerRef}
            sx={{ display: 'flex', flexWrap: 'nowrap' }}
            data-test={props.dataTest ?? props.id ?? props.name}
        >
            <Tooltip title={showTooltip ? props.tooltip : ''} enterDelay={1000}>
                <div style={{ width: '100%' }}>
                    <SelectComponent
                        // menuPlacement={menuPlacement as MenuPlacement}
                        // ToDo check menuPlacement="auto" and after this remove calc func above
                        menuPlacement={'auto'}
                        menuPosition={'fixed'}
                        {...selectParams}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.stopPropagation();
                            }
                        }}
                    />
                </div>
            </Tooltip>
        </Box>
    );
}

export default ReactSelect;
